/**
 * Drilldown for one regulated type (SPEC_Hazard_UI §2.3).
 *
 * Three sections stacked on one page, deliberately not sub-tabs: the panel is
 * already one level deep and tabs-in-tabs would be one too many.
 *
 * The release button sits above the assignment list, never in the rows. What is
 * released is the set — "these five and no other" — and a per-row release could
 * not cover what is missing, which is the part that matters.
 */

import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2, Trash2 } from "lucide-react";

import { useModalContext } from "../../components/ModalProvider";
import { useLegalAdministrationAPI } from "../../LegalAdministrationAPI";
import {
  HazardChip,
  SignoffPill,
  damageTypeLabel,
  formatDate,
} from "./hazardDisplay";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface Properties {
  regulatedObjectCode: string;
  onClose: (didChange: boolean) => void;
  onStartReview: (regulatedObjectCode: string) => void;
}

interface Member {
  hazardCode: string;
  relation: "carries" | "protects";
}

export default function HazardTypeDetailPanel(properties: Properties) {
  const { t } = useTranslation();
  const { pushError, pushSuccess } = useModalContext() as any;
  const legalAPI = useLegalAdministrationAPI();
  const fieldId = useId();

  const [detail, setDetail] = useState<any>(null);
  const [catalog, setCatalog] = useState<any[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [addCode, setAddCode] = useState<string | undefined>(undefined);
  const [openTrace, setOpenTrace] = useState<string | null>(null);
  const [overrideFor, setOverrideFor] = useState<string | null>(null);
  const [overrideValue, setOverrideValue] = useState<number | undefined>(undefined);
  const [overrideReason, setOverrideReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [didChange, setDidChange] = useState(false);

  const load = async () => {
    try {
      const [detailResult, catalogResult] = await Promise.all([
        legalAPI.getHazardTypeDetail(properties.regulatedObjectCode),
        legalAPI.getHazards(),
      ]);
      setDetail(detailResult);
      setCatalog(catalogResult?.hazards || []);
      setMembers(
        (detailResult?.assignments || []).map((assignment: any) => ({
          hazardCode: assignment.hazardCode,
          relation: assignment.relation,
        })),
      );
    } catch (error) {
      pushError(error);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties.regulatedObjectCode]);

  const savedMembers: Member[] = useMemo(
    () =>
      (detail?.assignments || []).map((assignment: any) => ({
        hazardCode: assignment.hazardCode,
        relation: assignment.relation,
      })),
    [detail],
  );

  /** Compared by content, so re-ordering alone is not treated as a change. */
  const dirty = useMemo(() => {
    const normalize = (list: Member[]) =>
      [...list].map((m) => `${m.hazardCode}:${m.relation}`).sort().join("|");
    return normalize(members) !== normalize(savedMembers);
  }, [members, savedMembers]);

  const hazardsByCode = useMemo(
    () => new Map(catalog.map((hazard: any) => [hazard.code, hazard])),
    [catalog],
  );

  const save = async () => {
    try {
      setSaving(true);
      await legalAPI.saveHazardAssignments({
        code: properties.regulatedObjectCode,
        members,
      });
      setDidChange(true);
      await load();
      pushSuccess(t("hazard.types.setSaved"));
    } catch (error) {
      pushError(error);
    } finally {
      setSaving(false);
    }
  };

  const submitOverride = async (hazardCode: string) => {
    if (!overrideValue || !overrideReason.trim()) return;
    try {
      await legalAPI.overrideHazardSeverity({
        regulatedObjectCode: properties.regulatedObjectCode,
        hazardCode,
        siBase: overrideValue,
        overrideReason: overrideReason.trim(),
      });
      setOverrideFor(null);
      setOverrideValue(undefined);
      setOverrideReason("");
      setDidChange(true);
      await load();
      pushSuccess(t("hazard.severity.overrideSaved"));
    } catch (error) {
      pushError(error);
    }
  };

  if (!detail) {
    return (
      <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-3">
        <Skeleton className="h-9 w-[200px]" />
        <Skeleton className="h-6 w-[320px] max-w-full" />
        <Skeleton className="h-[220px] w-full" />
      </div>
    );
  }

  const { regulatedObject, severities, reviews, permissions, signoffState, recertDueAt } = detail;
  const assignable = catalog.filter(
    (hazard: any) => !hazard.deprecated && !members.some((m) => m.hazardCode === hazard.code),
  );
  const addSelectId = `${fieldId}-add`;

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-6">
      <div className="flex flex-wrap">
        <Button
          variant="ghost"
          size="sm"
          className="gap-2 px-2 max-sm:w-full max-sm:justify-start"
          onClick={() => properties.onClose(didChange)}
        >
          <ArrowLeft aria-hidden className="h-4 w-4" />
          {t("hazard.types.backToList")}
        </Button>
      </div>

      {/* Header */}
      <div className="flex min-w-0 flex-wrap items-center gap-4">
        <div className="flex min-w-[240px] flex-1 flex-col gap-1">
          <h3 className="min-w-0 break-words text-lg font-semibold">{regulatedObject.name}</h3>
          <p className="min-w-0 break-words text-sm text-muted-foreground">
            {regulatedObject.code}
            {detail.assignments?.length
              ? ` · ${t("hazard.types.din276")}: ${
                  detail.assignments
                    .flatMap((a: any) => a.din276 || [])
                    .filter((anchor: string, index: number, all: string[]) => all.indexOf(anchor) === index)
                    .join(", ") || "—"
                }`
              : ""}
          </p>
        </div>
        <SignoffPill state={signoffState} t={t} />
        {permissions?.canSignOff ? (
          <Button
            className="max-sm:w-full"
            disabled={dirty}
            title={dirty ? t("hazard.types.saveBeforeRelease") : undefined}
            onClick={() => properties.onStartReview(properties.regulatedObjectCode)}
          >
            {t("hazard.types.releaseSet")}
          </Button>
        ) : null}
      </div>

      {/* Section: assignments */}
      <section className="flex min-w-0 flex-col gap-3">
        <h4 className="font-semibold">{t("hazard.types.sectionHazards")}</h4>

        {dirty ? (
          // Before saving, not after — the point is to warn while it can still
          // be reconsidered (§2.3).
          <Alert variant="warning">
            <AlertDescription>{t("hazard.types.changesResetRelease")}</AlertDescription>
          </Alert>
        ) : null}

        <div className="flex min-w-0 flex-wrap items-end gap-3">
          {permissions?.canCurate ? (
            <>
              <div className="flex w-[420px] max-w-full flex-col gap-1.5">
                <Label htmlFor={addSelectId}>{t("hazard.types.addHazard")}</Label>
                {/* Leerer Wert statt undefined: so bleibt das Feld kontrolliert
                    und zeigt nach dem Hinzufügen wieder den Platzhalter. */}
                <Select value={addCode ?? ""} onValueChange={(value: string) => setAddCode(value)}>
                  <SelectTrigger id={addSelectId}>
                    <SelectValue placeholder={t("hazard.types.addHazard")} />
                  </SelectTrigger>
                  <SelectContent className="max-w-[calc(100vw-2rem)]">
                    {assignable.map((hazard: any) => (
                      <SelectItem key={hazard.code} value={hazard.code} className="whitespace-normal">
                        {`${hazard.code} · ${hazard.label} (${damageTypeLabel(t, hazard.damageType)})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                className="max-sm:w-full"
                disabled={!addCode}
                onClick={() => {
                  if (!addCode) return;
                  setMembers([...members, { hazardCode: addCode, relation: "carries" }]);
                  setAddCode(undefined);
                }}
              >
                {t("hazard.types.add")}
              </Button>
            </>
          ) : null}
          {dirty && permissions?.canCurate ? (
            <>
              <Button className="gap-2 max-sm:w-full" disabled={saving} onClick={save}>
                {saving ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : null}
                {t("hazard.types.saveSet")}
              </Button>
              <Button
                variant="outline"
                className="max-sm:w-full"
                disabled={saving}
                onClick={() => setMembers(savedMembers)}
              >
                {t("hazard.types.discard")}
              </Button>
            </>
          ) : null}
        </div>

        {members.length === 0 ? (
          <div className="flex flex-col items-start gap-2">
            <p className="text-sm font-semibold">{t("hazard.types.noAssignmentsTitle")}</p>
            <p className="max-w-prose text-sm text-muted-foreground">
              {t("hazard.types.noAssignments")}
            </p>
          </div>
        ) : (
          <div className="min-w-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("hazard.types.hazard")}</TableHead>
                  <TableHead className="w-[140px]">{t("hazard.types.damageType")}</TableHead>
                  <TableHead className="w-[220px]">{t("hazard.types.relation")}</TableHead>
                  <TableHead className="w-[180px]">{t("hazard.types.origin")}</TableHead>
                  <TableHead className="w-[70px]">
                    <span className="sr-only">{t("hazard.types.removeHazard")}</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => {
                  const hazard = hazardsByCode.get(member.hazardCode);
                  const assignment = (detail.assignments || []).find(
                    (a: any) => a.hazardCode === member.hazardCode,
                  );
                  const relationSelectId = `${fieldId}-relation-${member.hazardCode}`;
                  return (
                    <TableRow key={member.hazardCode}>
                      <TableCell>
                        <HazardChip
                          label={`${member.hazardCode} · ${hazard?.label || member.hazardCode}`}
                          damageType={hazard?.damageType}
                          relation={member.relation}
                          t={t}
                        />
                      </TableCell>
                      <TableCell className="text-sm">
                        {damageTypeLabel(t, hazard?.damageType)}
                      </TableCell>
                      <TableCell>
                        {permissions?.canCurate ? (
                          <>
                            <Label htmlFor={relationSelectId} className="sr-only">
                              {`${t("hazard.types.relation")} — ${
                                hazard?.label || member.hazardCode
                              }`}
                            </Label>
                            <Select
                              value={member.relation}
                              onValueChange={(value: string) =>
                                setMembers(
                                  members.map((m) =>
                                    m.hazardCode === member.hazardCode
                                      ? { ...m, relation: value as Member["relation"] }
                                      : m,
                                  ),
                                )
                              }
                            >
                              <SelectTrigger id={relationSelectId} className="w-[190px] max-w-full">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="carries">
                                  {t("hazard.relation.carries")}
                                </SelectItem>
                                <SelectItem value="protects">
                                  {t("hazard.relation.protects")}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </>
                        ) : (
                          <span className="text-sm">{t(`hazard.relation.${member.relation}`)}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {assignment
                          ? t(`hazard.origin.${assignment.origin}`, { defaultValue: assignment.origin })
                          : t("hazard.origin.unsaved")}
                        {assignment?.confidence
                          ? ` · ${Math.round(assignment.confidence * 100)}%`
                          : ""}
                      </TableCell>
                      <TableCell>
                        {permissions?.canCurate ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`${t("hazard.types.removeHazard")} — ${
                              hazard?.label || member.hazardCode
                            }`}
                            title={t("hazard.types.removeHazard")}
                            onClick={() =>
                              setMembers(members.filter((m) => m.hazardCode !== member.hazardCode))
                            }
                          >
                            <Trash2 aria-hidden className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      {/* Section: severity */}
      <section className="flex min-w-0 flex-col gap-3">
        <h4 className="font-semibold">{t("hazard.types.sectionSeverity")}</h4>
        {(severities || []).length === 0 ? (
          <div className="flex flex-col items-start gap-2">
            <p className="text-sm font-semibold">{t("hazard.types.noSeverityTitle")}</p>
            <p className="max-w-prose text-sm text-muted-foreground">
              {t("hazard.types.noSeverity")}
            </p>
          </div>
        ) : (
          <div className="flex min-w-0 flex-col gap-2">
            {(severities || []).map((severity: any) => {
              const traceOpen = openTrace === severity.hazardCode;
              const overrideValueId = `${fieldId}-override-value-${severity.hazardCode}`;
              const overrideReasonId = `${fieldId}-override-reason-${severity.hazardCode}`;

              return (
                <div
                  key={severity.hazardCode}
                  className="flex min-w-0 flex-col gap-2 rounded-md border p-3"
                >
                  <div className="flex min-w-0 flex-wrap items-center gap-3">
                    <div className="min-w-[200px] flex-1 break-words">
                      <span className="font-semibold">{severity.label}</span>
                      <span className="text-sm text-muted-foreground">
                        {" "}
                        · {damageTypeLabel(t, severity.damageType)}
                      </span>
                      {severity.assessable ? (
                        <span className="ml-2 font-bold tabular-nums">{severity.siBase}</span>
                      ) : (
                        // Grey is not green: no severity is an open task, and it
                        // says why rather than showing a blank (§7).
                        <span
                          className="ml-2 rounded-sm border border-dashed px-1.5 text-xs text-muted-foreground"
                          title={t("hazard.severity.notAssessableHint")}
                        >
                          {t("hazard.severity.notAssessable")}
                        </span>
                      )}
                      {severity.origin === "manual_override" && severity.derivedSiBase ? (
                        <span className="ml-2 text-xs text-warning">
                          {t("hazard.severity.overriddenFrom", {
                            derived: severity.derivedSiBase,
                            value: severity.siBase,
                          })}
                        </span>
                      ) : null}
                    </div>
                    {severity.derivation ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-expanded={traceOpen}
                        onClick={() => setOpenTrace(traceOpen ? null : severity.hazardCode)}
                      >
                        {t("hazard.severity.derivation")}
                      </Button>
                    ) : null}
                    {permissions?.canCurate && severity.assessable ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-expanded={overrideFor === severity.hazardCode}
                        onClick={() => {
                          setOverrideFor(
                            overrideFor === severity.hazardCode ? null : severity.hazardCode,
                          );
                          setOverrideValue(undefined);
                          setOverrideReason("");
                        }}
                      >
                        {t("hazard.severity.override")}
                      </Button>
                    ) : null}
                  </div>

                  {/* Derivation trace — without it the review is guesswork. */}
                  {traceOpen && severity.derivation ? (
                    <div className="min-w-0 break-words rounded-md bg-muted p-3 font-mono text-xs">
                      <div>
                        {t("hazard.severity.legalDomain")}: {severity.derivation.legalDomain || "—"}
                      </div>
                      <div className="mt-2">
                        {t("hazard.severity.ceremonyScore")}: {severity.derivation.ceremonyScore ?? "—"}
                        {Array.isArray(severity.derivation.band)
                          ? ` · ${t("hazard.severity.band")} ${severity.derivation.band.join("/")}`
                          : ""}
                      </div>
                      {(severity.derivation.ceremonyBreakdown || []).map((part: any, index: number) => (
                        <div key={index} className="pl-4">
                          {part.evidence || part.label || part.component}{" "}
                          <span className="text-warning">
                            {part.points >= 0 ? "+" : ""}
                            {part.points}
                          </span>
                        </div>
                      ))}
                      <div className="mt-2">
                        {t("hazard.severity.contributingObligations")} (
                        {severity.derivation.contributingObligations?.length || 0})
                      </div>
                      {(severity.derivation.contributingObligations || []).map((obligation: any) => (
                        <div key={obligation.ref} className="pl-4">
                          {obligation.obligationKey || obligation.ref}
                          {obligation.description ? ` — ${obligation.description.slice(0, 90)}` : ""}
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {overrideFor === severity.hazardCode ? (
                    <div className="flex min-w-0 flex-wrap items-end gap-3">
                      <div className="flex w-[160px] max-w-full flex-col gap-1.5">
                        <Label htmlFor={overrideValueId}>{t("hazard.severity.selectValue")}</Label>
                        <Select
                          value={overrideValue !== undefined ? String(overrideValue) : ""}
                          onValueChange={(value: string) => setOverrideValue(Number(value))}
                        >
                          <SelectTrigger id={overrideValueId}>
                            <SelectValue placeholder={t("hazard.severity.selectValue")} />
                          </SelectTrigger>
                          <SelectContent>
                            {(severity.allowedSiBase || []).map((value: number) => (
                              <SelectItem key={value} value={String(value)}>
                                {String(value)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex min-w-[260px] flex-1 flex-col gap-1.5">
                        <Label htmlFor={overrideReasonId}>
                          {t("hazard.severity.reasonRequired")}
                        </Label>
                        <Input
                          id={overrideReasonId}
                          value={overrideReason}
                          onChange={(event) => setOverrideReason(event.target.value)}
                        />
                      </div>
                      <Button
                        className="max-sm:w-full"
                        disabled={!overrideValue || !overrideReason.trim()}
                        onClick={() => submitOverride(severity.hazardCode)}
                      >
                        {t("hazard.severity.applyOverride")}
                      </Button>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Section: history */}
      <section className="flex min-w-0 flex-col gap-2">
        <h4 className="font-semibold">{t("hazard.types.sectionHistory")}</h4>
        <p className="min-w-0 break-words text-sm text-muted-foreground">
          {t("hazard.types.setVersion")}: {detail.set?.version ?? "—"} ·{" "}
          {t("hazard.types.lastFullReview")}: {formatDate(detail.set?.lastFullReviewAt)}
          {recertDueAt ? ` · ${t("hazard.types.recertDue")}: ${formatDate(recertDueAt)}` : ""}
        </p>
        {(reviews || []).length === 0 ? (
          <p className="max-w-prose text-sm text-muted-foreground">
            {t("hazard.types.noReviews")}
          </p>
        ) : (
          <ul className="flex min-w-0 flex-col gap-1 text-sm">
            {(reviews || []).map((review: any) => (
              <li key={review._id} className="min-w-0 break-words">
                {formatDate(review.signedAt)} · {t(`hazard.review.mode.${review.mode}`)} ·{" "}
                {t(`hazard.review.decision.${review.decision}`)} · {review.signedBy}
                {review.qualification ? ` (${review.qualification})` : ""}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
